import React from 'react';
import { Cell, Pie, PieChart, Tooltip } from 'recharts';

import { Account } from '@ndvr/portfolio-api';

import {
    AllocationType,
    CashExposureType,
    ConvictionAllocationType,
    SecuredAllocationType,
} from '@/cip/_openapi/portfolio-optimizer';
import { GrowthAllocationType } from '@/cip/_openapi/portfolio-optimizer';
import { useRunAllocation } from '@/cip/api/optimization';
import { formatCurrencyShort } from 'utils';
import { ChartColors } from 'views/household/OverviewTab/constants';

import { CustomLegend } from './CustomLegend';
import { CustomTooltip } from './CustomTooltip';

// Create a sorting order map for allocation types (cash, secured, conviction, growth)
const ALLOCATION_TYPE_ORDER: Record<string, number> = [
    ...Object.values(CashExposureType),
    ...Object.values(SecuredAllocationType),
    ...Object.values(ConvictionAllocationType),
    ...Object.values(GrowthAllocationType),
].reduce(
    (acc, type, index) => {
        acc[type] = index;
        return acc;
    },
    {} as Record<string, number>,
);

const getAllocationTypeOrder = (allocationType: AllocationType): number => {
    return ALLOCATION_TYPE_ORDER[String(allocationType)] ?? Number.MAX_SAFE_INTEGER;
};

interface TargetAllocationChartProps {
    accountGroupGUID: string;
    accounts?: Account[];
    selectedAccountGUID?: string;
}

export const TargetAllocationChart = ({
    accountGroupGUID,
    accounts = [],
    selectedAccountGUID,
}: TargetAllocationChartProps) => {
    const [hoveredAccountGUID, setHoveredAccountGUID] = React.useState<string | undefined>();
    const { data: allocationRun, isLoading } = useRunAllocation(accountGroupGUID);
    const allocationData = allocationRun?.targetAllocations ?? [];

    const accountNameMap = accounts.reduce(
        (acc, account) => {
            acc[account.accountGUID] = account.accountName?.plainText || account.accountGUID;
            return acc;
        },
        {} as Record<string, string>,
    );

    if (isLoading) {
        return <div className="flex h-96 items-center justify-center">Loading allocation data...</div>;
    }

    if (!allocationData.length) {
        return <div className="flex h-96 items-center justify-center text-gray-500">No allocation data available</div>;
    }

    // Create a map of strategy names to their allocation types for sorting
    const strategyToAllocationType = allocationData.reduce(
        (acc, item) => {
            if (!acc[item.strategyName]) {
                acc[item.strategyName] = item.allocationType;
            }
            return acc;
        },
        {} as Record<string, AllocationType>,
    );

    const allStrategyNames = [...new Set(allocationData.map((item) => item.strategyName))].sort((a, b) => {
        return (
            getAllocationTypeOrder(strategyToAllocationType[a]) - getAllocationTypeOrder(strategyToAllocationType[b])
        );
    });
    const strategyColorMap = allStrategyNames.reduce(
        (acc, strategyName, index) => {
            acc[strategyName] = ChartColors[index % ChartColors.length];
            return acc;
        },
        {} as Record<string, string>,
    );

    const portfolioData = allocationData
        .reduce(
            (acc, item) => {
                const existing = acc.find((entry) => entry.name === item.strategyName);
                if (existing) {
                    existing.value += item.targetExposure;
                } else {
                    acc.push({
                        name: item.strategyName,
                        value: item.targetExposure,
                        allocationType: item.allocationType,
                    });
                }
                return acc;
            },
            [] as Array<{ name: string; value: number; allocationType: AllocationType }>,
        )
        .sort((a, b) => {
            return getAllocationTypeOrder(a.allocationType) - getAllocationTypeOrder(b.allocationType);
        });

    const portfolioTotal = portfolioData.reduce((sum, item) => sum + item.value, 0);

    // When account is hovered, split each strategy into hovered and other portions
    const displayPortfolioData = hoveredAccountGUID
        ? portfolioData.flatMap((item) => {
              // Sum all allocations for this account and strategy (there may be multiple allocation types)
              const hoveredAccountValue = allocationData
                  .filter(
                      (allocItem) =>
                          allocItem.accountGuid === hoveredAccountGUID && allocItem.strategyName === item.name,
                  )
                  .reduce((sum, allocItem) => sum + allocItem.targetExposure, 0);

              const otherAccountsValue = item.value - hoveredAccountValue;

              const segments = [];
              if (hoveredAccountValue > 0) {
                  segments.push({
                      name: item.name,
                      value: hoveredAccountValue,
                      allocationType: item.allocationType,
                      isFromSelectedAccount: true,
                      selectedAccountValue: hoveredAccountValue,
                  });
              }
              if (otherAccountsValue > 0) {
                  segments.push({
                      name: item.name,
                      value: otherAccountsValue,
                      allocationType: item.allocationType,
                      isFromSelectedAccount: false,
                      selectedAccountValue: hoveredAccountValue,
                  });
              }
              return segments;
          })
        : portfolioData.map((item) => ({ ...item, selectedAccountValue: null, isFromSelectedAccount: null }));

    // Get selected account chart data if an account is selected
    const selectedAccountData = selectedAccountGUID
        ? (() => {
              const accountAllocations = allocationData.filter((item) => item.accountGuid === selectedAccountGUID);
              const accountTotal = accountAllocations.reduce((sum, item) => sum + item.targetExposure, 0);

              // Aggregate by strategy name (same strategy can have multiple allocation types)
              const aggregatedData = accountAllocations.reduce(
                  (acc, item) => {
                      const existing = acc.find((entry) => entry.name === item.strategyName);
                      if (existing) {
                          existing.value += item.targetExposure;
                      } else {
                          acc.push({
                              name: item.strategyName,
                              value: item.targetExposure,
                              allocationType: item.allocationType,
                          });
                      }
                      return acc;
                  },
                  [] as Array<{ name: string; value: number; allocationType: AllocationType }>,
              );

              // Sort account data in the same order as portfolio strategies
              const sortedData = aggregatedData.sort((a, b) => {
                  const indexA = allStrategyNames.indexOf(a.name);
                  const indexB = allStrategyNames.indexOf(b.name);
                  return indexA - indexB;
              });

              const data = {
                  accountGuid: selectedAccountGUID,
                  data: sortedData,
                  total: accountTotal,
              };

              return data;
          })()
        : null;

    return (
        <div className="rounded-lg border bg-white p-6">
            <h3 className="text-md font-semibold">Portfolio Level Target Allocation</h3>
            <div className="relative">
                <div className="h-[300px] w-full">
                    <PieChart
                        responsive
                        style={{ width: '100%', height: '100%' }}
                        className="[&_*]:!outline-none [&_.recharts-pie-sector]:!cursor-default [&_.recharts-sector]:transition-opacity [&_.recharts-sector]:duration-900 [&_.recharts-sector]:ease-in-out [&_.recharts-sector]:!outline-none"
                    >
                        <Pie
                            data={displayPortfolioData}
                            cx="50%"
                            cy="50%"
                            innerRadius="50%"
                            outerRadius="70%"
                            paddingAngle={0}
                            dataKey="value"
                            stroke="#fff"
                            strokeWidth={2}
                            startAngle={90}
                            endAngle={-270}
                            isAnimationActive={false}
                            activeShape={false}
                        >
                            {displayPortfolioData.map((entry, index) => {
                                const isDimmed = entry.isFromSelectedAccount === false;
                                return (
                                    <Cell
                                        key={`${entry.name}-${index}`}
                                        fill={strategyColorMap[entry.name]}
                                        opacity={isDimmed ? 0.2 : 1}
                                        stroke={'#fff'}
                                        strokeWidth={2}
                                    />
                                );
                            })}
                        </Pie>
                        <Tooltip content={<CustomTooltip total={portfolioTotal} />} wrapperStyle={{ zIndex: 1000 }} />
                    </PieChart>
                </div>
                <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
                    <div className="text-center">
                        <div className="text-sm text-gray-600">Total</div>
                        <div className="text-xl font-bold">{formatCurrencyShort(portfolioTotal, 'USD', 1)}</div>
                    </div>
                </div>
            </div>

            {selectedAccountData && selectedAccountData.total > 0 && (
                <div
                    className="mt-2"
                    onMouseEnter={() => setHoveredAccountGUID(selectedAccountData.accountGuid)}
                    onMouseLeave={() => setHoveredAccountGUID(undefined)}
                >
                    <h3 className="text-md font-semibold">
                        {accountNameMap[selectedAccountData.accountGuid] || selectedAccountData.accountGuid}
                    </h3>
                    <div className="relative">
                        <div className="h-[300px] w-full">
                            <PieChart
                                responsive
                                style={{ width: '100%', height: '100%' }}
                                className="[&_*]:!outline-none [&_.recharts-pie-sector]:!cursor-default [&_.recharts-sector]:!outline-none"
                            >
                                <Pie
                                    data={selectedAccountData.data}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius="50%"
                                    outerRadius="70%"
                                    paddingAngle={1}
                                    dataKey="value"
                                    stroke="#fff"
                                    strokeWidth={1}
                                    startAngle={90}
                                    endAngle={-270}
                                    isAnimationActive={true}
                                    activeShape={false}
                                    animationDuration={500}
                                >
                                    {selectedAccountData.data.map((entry, index) => (
                                        <Cell key={`${entry.name}-${index}`} fill={strategyColorMap[entry.name]} />
                                    ))}
                                </Pie>
                                <Tooltip
                                    content={<CustomTooltip total={selectedAccountData.total} />}
                                    wrapperStyle={{ zIndex: 1000 }}
                                />
                            </PieChart>
                        </div>
                        <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
                            <div className="text-center">
                                <div className="text-sm text-gray-600">Total</div>
                                <div className="text-xl font-bold">
                                    {formatCurrencyShort(selectedAccountData.total, 'USD', 1)}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="mt-6">
                <CustomLegend
                    payload={allStrategyNames.map((name) => ({ value: name }))}
                    strategyColorMap={strategyColorMap}
                />
            </div>
        </div>
    );
};
